import { useTaskPlannedOccurrenceForDate } from '@griply/hooks';
import { Task, Timestamp, UpdateTask } from '@griply/types';
import React, { useMemo, useRef } from 'react';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import { useToday } from 'shared/contexts/today';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useUser } from 'shared/hooks/use-user';
import { DataType } from 'shared/types/data-type';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { getScheduleByDate } from 'shared/utils/schedules';
import { useTheme } from 'styled-components';

export type ContextMenuProps = {
  children: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  task: Task;
  hideMeta?: boolean;
  hideEdit?: boolean;
  timeslotDate?: Timestamp;
  disabled?: boolean;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
};

export const ContextMenu: React.FC<ContextMenuProps> = ({
  children,
  task,
  onOpen,
  onClose,
  hideMeta,
  hideEdit,
  timeslotDate,
  disabled,
  onUpdate,
  onDelete,
  onDuplicate,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const { position, open, close } = useContextMenuState({
    disabled: theme.isMobile || !!task.frozenAt,
    onOpen: () => onOpen?.(),
    onClose: () => onClose?.(),
  });

  const activeSchedule = useMemo(
    () =>
      task.schedules
        ? timeslotDate
          ? getScheduleByDate(task.schedules, { date: timeslotDate, today })
          : getActiveSchedule(task.schedules, today)
        : undefined,
    [task.schedules, timeslotDate, today],
  );
  const plannedOccurrence = useTaskPlannedOccurrenceForDate(
    task,
    timeslotDate ?? today,
    { weekStartsOn, today },
  );

  return (
    <div
      ref={containerRef}
      data-component-type={DataType.Task}
      data-id={task.id}
      data-title={task.name}
      data-duration={
        activeSchedule?.timeslot?.duration ?? task.timeslot?.duration
      }
      onContextMenu={disabled ? undefined : open}
    >
      {children}
      {!disabled && !!position && (
        <TaskCardContextMenu
          task={task}
          onClose={close}
          location={position}
          hideMeta={hideMeta}
          hideEdit={hideEdit}
          timeslotDate={plannedOccurrence?.date ?? timeslotDate}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
        />
      )}
    </div>
  );
};
